import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CSVLink } from 'react-csv';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import '../styles/DoctorSalesSummary.css';

const DoctorSalesSummary = () => {
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(localStorage.getItem('selectedDoctor') || ''); // Preserve doctor
  const [startDate, setStartDate] = useState(localStorage.getItem('startDate') ? new Date(localStorage.getItem('startDate')) : new Date()); // Preserve startDate
  const [endDate, setEndDate] = useState(localStorage.getItem('endDate') ? new Date(localStorage.getItem('endDate')) : new Date()); // Preserve endDate
  const [salesData, setSalesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();  

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}${process.env.REACT_APP_API_GET_DOCTORS}`);
        setDoctors(response.data.doctors);
      } catch (error) {
        console.error("Error fetching doctors:", error);
      }
    };

    fetchDoctors();
  }, []);

  const handleSearch = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}${process.env.REACT_APP_API_DOCTOR_SALES}`, {
        params: {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
          doctorName: selectedDoctor,
        },
      });
      setSalesData(response.data);
    } catch (error) {
      console.error('Error fetching doctor sales summary:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const CATEGORY_FIELDS = [
    'Total_Consultations',
    'Total_Admissions',
    'Total_Observations',
    'Total_Surgeries',
    'Total_Procedures',
    'Total_Ultrasound',
    'Total_Echocardiogram',
    'Total_Lab',
    'Total_Medication',
    'Total_Radiology',
  ];

  const calculateTotal = (row) => {
    return CATEGORY_FIELDS.reduce(
      (sum, field) => sum + parseFloat(row[field] || 0),
      0
    );
  };

  const formatAmount = (value) => {
    const num = Math.round(parseFloat(value || 0));
    if (!Number.isFinite(num)) return '0';
    return num.toLocaleString('en-US');
  };

  const calculateGrandTotals = () => {
    const totals = CATEGORY_FIELDS.reduce((acc, field) => {
      acc[field] = salesData.reduce(
        (sum, row) => sum + parseFloat(row[field] || 0),
        0
      );
      return acc;
    }, {});
    totals.Total = CATEGORY_FIELDS.reduce((sum, field) => sum + totals[field], 0);
    return totals;
  };

  const grandTotals = salesData.length > 0 ? calculateGrandTotals() : null;

  const getCsvData = () => {
    const rows = salesData.map((row) => ({
      doctor_name: row.doctor_name,
      Total: calculateTotal(row),
      Total_Consultations: row.Total_Consultations,
      Total_Admissions: row.Total_Admissions,
      Total_Observations: row.Total_Observations,
      Total_Surgeries: row.Total_Surgeries,
      Total_Procedures: row.Total_Procedures,
      Total_Ultrasound: row.Total_Ultrasound,
      Total_Echocardiogram: row.Total_Echocardiogram,
      Total_Lab: row.Total_Lab,
      Total_Medication: row.Total_Medication,
      Total_Radiology: row.Total_Radiology,
    }));

    if (grandTotals) {
      rows.unshift({
        doctor_name: 'Grand Total',
        Total: grandTotals.Total,
        Total_Consultations: grandTotals.Total_Consultations,
        Total_Admissions: grandTotals.Total_Admissions,
        Total_Observations: grandTotals.Total_Observations,
        Total_Surgeries: grandTotals.Total_Surgeries,
        Total_Procedures: grandTotals.Total_Procedures,
        Total_Ultrasound: grandTotals.Total_Ultrasound,
        Total_Echocardiogram: grandTotals.Total_Echocardiogram,
        Total_Lab: grandTotals.Total_Lab,
        Total_Medication: grandTotals.Total_Medication,
        Total_Radiology: grandTotals.Total_Radiology,
      });
    }

    return rows;
  };

  const handleShowCommission = (row) => {
    
    localStorage.setItem('selectedDoctor', selectedDoctor);
    localStorage.setItem('startDate', startDate.toISOString());
    localStorage.setItem('endDate', endDate.toISOString());
    navigate(`/doctor-sales-details?doctor=${row.doctor_name}&startDate=${startDate.toISOString().split('T')[0]}&endDate=${endDate.toISOString().split('T')[0]}`);
  };

  const handleShowDetail = (row) => {
    localStorage.setItem('selectedDoctor', selectedDoctor);
    localStorage.setItem('startDate', startDate.toISOString());
    localStorage.setItem('endDate', endDate.toISOString());
    navigate(`/doctor-sales-details?doctor=${row.doctor_name}&startDate=${startDate.toISOString().split('T')[0]}&endDate=${endDate.toISOString().split('T')[0]}`);
  };

  const columns = [
    { name: 'Doctor Name', selector: row => row.doctor_name, sortable: true },
    { 
      name: 'Total', 
      cell: (row) => formatAmount(calculateTotal(row)),
      sortable: true,
      sortFunction: (rowA, rowB) => calculateTotal(rowB) - calculateTotal(rowA),
      style: {
        backgroundColor: '#f2f2f2',
        fontWeight: 'bold',
      }
    },
    { name: 'Consultations', selector: row => row.Total_Consultations, cell: (row) => formatAmount(row.Total_Consultations), sortable: true },
    { name: 'Admissions', selector: row => row.Total_Admissions, cell: (row) => formatAmount(row.Total_Admissions), sortable: true },
    { name: 'Observations', selector: row => row.Total_Observations, cell: (row) => formatAmount(row.Total_Observations), sortable: true },
    { name: 'Surgeries', selector: row => row.Total_Surgeries, cell: (row) => formatAmount(row.Total_Surgeries), sortable: true },
    { name: 'Procedure', selector: row => row.Total_Procedures, cell: (row) => formatAmount(row.Total_Procedures), sortable: true },
    { name: 'Ultrasound', selector: row => row.Total_Ultrasound, cell: (row) => formatAmount(row.Total_Ultrasound), sortable: true },
    { name: 'Echocardiogram', selector: row => row.Total_Echocardiogram, cell: (row) => formatAmount(row.Total_Echocardiogram), sortable: true },
    { name: 'Lab', selector: row => row.Total_Lab, cell: (row) => formatAmount(row.Total_Lab), sortable: true },
    { name: 'Medication', selector: row => row.Total_Medication, cell: (row) => formatAmount(row.Total_Medication), sortable: true },
    { name: 'Radiology', selector: row => row.Total_Radiology, cell: (row) => formatAmount(row.Total_Radiology), sortable: true },
    { 
      name: '', 
      cell: (row) => (
        row.doctor_name === 'Unassigned' ? null : (
          <button onClick={() => handleShowCommission(row)} className="icon-button">
            <FontAwesomeIcon icon={faDollarSign} />
          </button>
        )
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    { 
      name: '', 
      cell: (row) => (
        row.doctor_name === 'Unassigned' ? null : (
          <button onClick={() => handleShowDetail(row)} className="icon-button">
            <FontAwesomeIcon icon={faInfoCircle} />
          </button>
        )
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    }
  ];

  return (
    <div className="doctor-sales-summary">
      <h2>Doctor Sales Summary</h2>
      <div className="filter-container">
        <div className="filter-group">
          <label>Start Date:</label>
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            dateFormat="yyyy-MM-dd"
          />
        </div>
        <div className="filter-group">
          <label>End Date:</label>
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            dateFormat="yyyy-MM-dd"
          />
        </div>
        <div className="filter-group">
          <label>Select Doctor:</label>
          <select
            value={selectedDoctor}
            onChange={(e) => setSelectedDoctor(e.target.value)}
          >
            <option value="">All Doctors</option>
            {doctors.map((doctor) => (
              <option key={doctor.name} value={doctor.name}>
                {doctor.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <button onClick={handleSearch} disabled={isLoading}>
            {isLoading ? 'Running Report...' : 'Run Report'}
          </button>
        </div>
      </div>

      {grandTotals && (
        <div className="grand-totals-container">
          <table className="grand-totals-table">
            <thead>
              <tr>
                <th>Doctor Name</th>
                <th>Total</th>
                <th>Consultations</th>
                <th>Admissions</th>
                <th>Observations</th>
                <th>Surgeries</th>
                <th>Procedure</th>
                <th>Ultrasound</th>
                <th>Echocardiogram</th>
                <th>Lab</th>
                <th>Medication</th>
                <th>Radiology</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Grand Total</td>
                <td>{formatAmount(grandTotals.Total)}</td>
                <td>{formatAmount(grandTotals.Total_Consultations)}</td>
                <td>{formatAmount(grandTotals.Total_Admissions)}</td>
                <td>{formatAmount(grandTotals.Total_Observations)}</td>
                <td>{formatAmount(grandTotals.Total_Surgeries)}</td>
                <td>{formatAmount(grandTotals.Total_Procedures)}</td>
                <td>{formatAmount(grandTotals.Total_Ultrasound)}</td>
                <td>{formatAmount(grandTotals.Total_Echocardiogram)}</td>
                <td>{formatAmount(grandTotals.Total_Lab)}</td>
                <td>{formatAmount(grandTotals.Total_Medication)}</td>
                <td>{formatAmount(grandTotals.Total_Radiology)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="data-table-container">
        <DataTable
          columns={columns}
          data={salesData}
          pagination
          highlightOnHover
          progressPending={isLoading}
          defaultSortField="Total"
          defaultSortAsc={false}
        />
      </div>

      {salesData.length > 0 && (
        <CSVLink
          data={getCsvData()}
          filename={`doctor_sales_summary_${startDate.toISOString().split('T')[0]}_${endDate.toISOString().split('T')[0]}.csv`}
          className="btn btn-primary"
        >
          Export to CSV
        </CSVLink>
      )}
    </div>
  );
};

export default DoctorSalesSummary;
